import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled, { css } from "styled-components";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Popover } from "@headlessui/react";
import {
  formatCurrencyInput,
  processCurrencyInput,
  formatCurrencyOnBlur,
  getCurrencyValue,
} from "../../../utils/formatters";
import { theme } from "../../../theme";
import { Z_INDEX, TRANSITIONS, BORDER_RADIUS } from "../../../constants";
import { useTheme } from "../../../contexts/ThemeContext";

import logger from '../../../utils/logger';

// Styled Components
const AnalysisContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  background: transparent;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fonts.heading};
  font-size: ${theme.typography.fontSizes["2xl"]};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${props => props.isDarkMode ? 'rgb(229, 231, 235)' : theme.colors.neutral[900]};
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

const Table = styled.table`
  width: 100%;
  height: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.fontSizes.xs};
  flex: 1;
  display: flex;
  flex-direction: column;

  thead,
  tbody {
    display: block;
  }

  thead {
    flex-shrink: 0;
  }

  tbody {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  tr {
    display: flex;
    width: 100%;
    min-height: 1.75rem;
  }

  th,
  td {
    flex: 1;
    display: flex;
    align-items: center;
    padding: 0.125rem 0.25rem;
  }

  th:first-child,
  td:first-child {
    flex: 1.2;
  }

  th:last-child,
  td:last-child {
    flex: 0.8;
  }
`;

const Th = styled.th`
  text-align: left;
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${props => props.isDarkMode ? 'rgb(209, 213, 219)' : theme.colors.neutral[700]};
  font-size: ${theme.typography.fontSizes.xs};
`;

const Td = styled.td`
  color: ${props => props.isDarkMode ? 'rgb(209, 213, 219)' : theme.colors.neutral[700]};
  font-size: ${theme.typography.fontSizes.xs};

  &:first-child {
    font-weight: ${theme.typography.fontWeights.medium};
  }
`;

const Tr = styled.tr`
  transition: ${TRANSITIONS.fast};

  &:hover {
    background-color: ${props => props.isDarkMode ? 'rgb(55, 65, 81)' : theme.colors.neutral[50]};
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 1.5rem;
  padding: 0.125rem 0.25rem;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  border-radius: 0.25rem;
  font-size: ${theme.typography.fontSizes.xs};
  color: ${props => props.isDarkMode ? 'rgb(229, 231, 235)' : theme.colors.neutral[900]};
  background: ${props => props.isDarkMode ? 'rgb(31, 41, 55)' : 'white'};
  transition: ${TRANSITIONS.fast};

  &:hover {
    border-color: ${props => props.isDarkMode ? 'rgb(107, 114, 128)' : theme.colors.neutral[400]};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

const InfoIcon = styled(InformationCircleIcon)`
  width: 1.25rem;
  height: 1.25rem;
  color: ${props => props.theme.isDarkMode ? 'rgb(156, 163, 175)' : theme.colors.neutral[400]};
  margin-left: ${theme.spacing[2]};
  transition: ${TRANSITIONS.fast};
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.isDarkMode ? 'rgb(209, 213, 219)' : theme.colors.primary[500]};
  }
`;

const Tooltip = styled(Popover.Panel)`
  position: absolute;
  z-index: ${Z_INDEX.tooltip};
  width: 32rem;
  max-width: calc(100vw - ${theme.spacing[8]});
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  background: ${props => props.isDarkMode ? 'rgb(31, 41, 55)' : 'white'};
  border-radius: ${BORDER_RADIUS.lg};
  box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
  font-size: ${theme.typography.fontSizes.sm};
  line-height: 1.5;
  color: ${props => props.isDarkMode ? 'rgb(209, 213, 219)' : theme.colors.neutral[700]};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  animation: tooltipFade ${theme.animations.durations.normal}
    ${theme.animations.timings["ease-out"]};

  &::before {
    content: "";
    position: absolute;
    top: -6px;
    left: 24px;
    width: 12px;
    height: 12px;
    background: ${props => props.isDarkMode ? 'rgb(31, 41, 55)' : 'white'};
    transform: rotate(45deg);
    borderborder-left: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
    borderborder-top: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  }

  p {
    margin: ${theme.spacing[2]} 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  user-select: none;
  color: ${props => props.isDarkMode ? 'rgb(209, 213, 219)' : theme.colors.neutral[700]};
  font-size: ${theme.typography.fontSizes.xs};
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  border-radius: 0.25rem;
  cursor: pointer;
  background-color: ${props => props.isDarkMode ? 'rgb(31, 41, 55)' : 'white'};

  &:checked {
    background-color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

// Fine-tune the lookup tables to be slightly more aggressive than before
const CONVERSION_LOOKUP = {
  Single: {
    SS_RANGES: {
      LOW: { max: 25, multipliers: [0.978, 0.953, 0.928, 0.903, 0.878] },
      MEDIUM: { max: 50, multipliers: [0.953, 0.928, 0.903, 0.878, 0.853] },
      HIGH: { max: 85, multipliers: [0.928, 0.903, 0.878, 0.853, 0.828] },
    },
    INCOME_RANGES: [0, 20000, 40000, 60000, 80000, 100000],
  },
  "Married Filing Jointly": {
    SS_RANGES: {
      LOW: { max: 25, multipliers: [0.978, 0.953, 0.928, 0.903, 0.878] },
      MEDIUM: { max: 50, multipliers: [0.953, 0.928, 0.903, 0.878, 0.853] },
      HIGH: { max: 85, multipliers: [0.928, 0.903, 0.878, 0.853, 0.828] },
    },
    INCOME_RANGES: [0, 40000, 80000, 120000, 160000, 200000],
  },
};

// Currency Input Component
const CurrencyInput = ({ value, onChange, onBlur, isDarkMode }) => {
  const [displayValue, setDisplayValue] = useState(formatCurrencyOnBlur(value));

  const handleChange = (e) => {
    let newValue = e.target.value.replace(/[^0-9.]/g, "");

    // Handle decimal points
    const decimalCount = (newValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const parts = newValue.split(".");
      newValue = parts[0] + "." + parts.slice(1).join("");
    }

    // Limit to 2 decimal places
    if (newValue.includes(".")) {
      const parts = newValue.split(".");
      newValue = parts[0] + "." + parts[1].slice(0, 2);
    }

    // Format for display
    let formattedValue = newValue;
    if (formattedValue !== "") {
      if (formattedValue === ".") {
        formattedValue = "$0.";
      } else if (formattedValue.startsWith(".")) {
        formattedValue = "$0" + formattedValue;
      } else {
        formattedValue = "$" + formattedValue;
      }
    }

    setDisplayValue(formattedValue);
    onChange(parseFloat(newValue) || 0);
  };

  const handleBlur = (e) => {
    const formattedValue = formatCurrencyOnBlur(e.target.value);
    setDisplayValue(formattedValue);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleFocus = (e) => {
    // On focus, strip the formatting but keep the numeric value
    const numericString = e.target.value.replace(/[^\d.]/g, "");
    setDisplayValue("$" + numericString);
  };

  return (
    <Input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder="$0.00"
      isDarkMode={isDarkMode}
    />
  );
};

// Enhanced TableRow Component
const EnhancedTableRow = ({
  label,
  value,
  info,
  isEditable,
  onChange,
  isCurrency,
  isCheckbox,
  isDarkMode,
}) => {
  return (
    <Tr isDarkMode={isDarkMode}>
      <Td isDarkMode={isDarkMode}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {info && (
            <Popover className="relative">
              <Popover.Button as="div">
                <InfoIcon aria-label={`${label} Information`} />
              </Popover.Button>
              <Tooltip isDarkMode={isDarkMode}>
                {info.split(".").map(
                  (sentence, index) =>
                    sentence.trim() && (
                      <p
                        key={index}
                        style={{ marginTop: index > 0 ? theme.spacing[2] : 0 }}
                      >
                        {sentence.trim() + "."}
                      </p>
                    ),
                )}
              </Tooltip>
            </Popover>
          )}
          {label}
        </div>
      </Td>
      <Td isDarkMode={isDarkMode}>
        {isEditable ? (
          isCheckbox ? (
            <CheckboxContainer>
              {Array.isArray(value) &&
                value.map((isChecked, index) => (
                  <CheckboxLabel key={index} isDarkMode={isDarkMode}>
                    <Checkbox
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onChange(index)}
                      isDarkMode={isDarkMode}
                    />
                  </CheckboxLabel>
                ))}
            </CheckboxContainer>
          ) : isCurrency ? (
            <CurrencyInput value={value} onChange={onChange} isDarkMode={isDarkMode} />
          ) : (
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              aria-label={`${label} input`}
              isDarkMode={isDarkMode}
            />
          )
        ) : (
          <span>{value}</span>
        )}
      </Td>
    </Tr>
  );
};

const HouseholdTaxAnalysis = ({
  year,
  initialTaxFilingStatus = "Single",
  deductionsData,
  birthdates,
  incomeData,
  taxBrackets,
  onTaxableIncomeChange,
  totalFromIncomeAnalysis,
  frequencyData,
  onBlindExemptionChange,
  hideTitle = false,
}) => {
  const { isDarkMode } = useTheme();
  const [taxFilingStatus, setTaxFilingStatus] = useState(initialTaxFilingStatus);
  const [blindDeductionCheckboxes, setBlindDeductionCheckboxes] = useState([]);
  const [values, setValues] = useState({
    projectedAGI: 0,
    projectedMAGI: 0,
    deduction65: 0,
    blindDeduction: 0,
    standardDeduction: 13850,
    currentTaxableIncome: 0,
    currentTaxBracketCeiling: 0,
    currentSSTaxability: "0%",
    grossConversionMaxCurrentBracket: "$0.00 (0%)",
    grossConversionMaxThroughNextBracket: "$0.00 (0%)",
  });
  const [projectedCapitalGains, setProjectedCapitalGains] = useState(0);
  const [rothConversion, setRothConversion] = useState(0);

  useEffect(() => {
    initializeBlindDeductionCheckboxes();
  }, [taxFilingStatus]);

  useEffect(() => {
    recalculateAll();
  }, [
    taxFilingStatus,
    blindDeductionCheckboxes,
    birthdates,
    incomeData,
    taxBrackets,
    rothConversion,
  ]);

  const initializeBlindDeductionCheckboxes = () => {
    const numCheckboxes = taxFilingStatus === "Married Filing Jointly" ? 2 : 1;
    setBlindDeductionCheckboxes(new Array(numCheckboxes).fill(false));
  };

  const handleCheckboxChange = (index) => {
    const updatedCheckboxes = [...blindDeductionCheckboxes];
    updatedCheckboxes[index] = !updatedCheckboxes[index];
    setBlindDeductionCheckboxes(updatedCheckboxes);

    // Notify parent of blind exemption state
    if (onBlindExemptionChange) {
      onBlindExemptionChange(updatedCheckboxes.some((isChecked) => isChecked));
    }
  };

  const formatAsCurrency = (value) =>
    `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const calculateAge = (dateOfBirth) => {
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate.getTime())) {
      // logger.error("Invalid date format for dateOfBirth:", dateOfBirth);
      return NaN;
    }
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const getDeductionAmount = (filingStatus, type) => {
    if (!deductionsData || !Array.isArray(deductionsData)) {
      // logger.error("No valid deductions data provided");
      return 0;
    }

    const deduction = deductionsData.find((d) => d.status === filingStatus);
    if (!deduction) {
      logger.error("No deduction found for filing status:", filingStatus);
      return 0;
    }

    const amount = parseFloat(deduction[type].replace(/[$,]/g, ""));
    return amount;
  };

  const calculateDeduction65 = () => {
    // logger.group("📊 65+ Deduction Calculation");

    const deductionAmount = getDeductionAmount(taxFilingStatus, "over65");
    // logger.info("Base deduction amount:", formatAsCurrency(deductionAmount));

    let eligiblePeople = 0;

    if (birthdates.clientBirthdate) {
      const clientAge = calculateAge(birthdates.clientBirthdate);
      // logger.info("Client age:", clientAge);
      if (clientAge >= 65) {
        eligiblePeople++;
        // logger.info("Client is eligible for 65+ deduction");
      }
    }

    if (
      taxFilingStatus === "Married Filing Jointly" &&
      birthdates.spouseBirthdate
    ) {
      const spouseAge = calculateAge(birthdates.spouseBirthdate);
      // logger.info("Spouse age:", spouseAge);
      if (spouseAge >= 65) {
        eligiblePeople++;
        // logger.info("Spouse is eligible for 65+ deduction");
      }
    }

    const totalDeduction = deductionAmount * eligiblePeople;
    // logger.info("Total 65+ deduction:", formatAsCurrency(totalDeduction));

    // logger.groupEnd();
    return totalDeduction;
  };

  const calculateBlindDeduction = () => {
    // logger.group("📊 Blind Deduction Calculation");

    const deductionAmount = getDeductionAmount(taxFilingStatus, "blind");
    // logger.info("Base blind deduction amount:", formatAsCurrency(deductionAmount));

    const checkedCount = blindDeductionCheckboxes.filter(
      (isChecked) => isChecked,
    ).length;
    // logger.info("Number of blind exemptions:", checkedCount);

    const totalDeduction = deductionAmount * checkedCount;
    // logger.info("Total blind deduction:", formatAsCurrency(totalDeduction));

    // logger.groupEnd();
    return totalDeduction;
  };

  const getFrequencyMultiplier = (income) => {
    // logger.group("📅 Calculating Frequency Multiplier");
    // logger.info("Income:", income);

    let multiplier = 1;

    if (income.custom_frequency_days) {
      // Calculate how many times this occurs in a year
      multiplier = 365 / parseFloat(income.custom_frequency_days);
      // logger.info("Custom frequency days:", income.custom_frequency_days);
      // logger.info("Calculated annual multiplier:", multiplier);
    } else if (income.frequency_id) {
      // Use the frequency_id to determine the multiplier
      switch (income.frequency_id) {
        case 12: // Monthly
          multiplier = 12;
          break;
        case 52: // Weekly
          multiplier = 52;
          break;
        case 26: // Bi-weekly
          multiplier = 26;
          break;
        case 24: // Semi-monthly
          multiplier = 24;
          break;
        case 4: // Quarterly
          multiplier = 4;
          break;
        case 2: // Semi-annually
          multiplier = 2;
          break;
        case 1: // Annually
          multiplier = 1;
          break;
        default:
          // If we have a frequency_multiplier, use that
          if (income.frequency_multiplier) {
            multiplier = income.frequency_multiplier;
          }
      }
      // logger.info("Frequency ID:", income.frequency_id);
      // logger.info("Frequency name:", income.frequency_name);
      // logger.info("Calculated annual multiplier:", multiplier);
    }

    // logger.info("Final multiplier:", multiplier);
    // logger.groupEnd();
    return multiplier;
  };

  const parseCurrencyValue = (value) => {
    if (!value) return 0;
    const parsed = parseFloat(value.toString().replace(/[$,]/g, "")) || 0;
    // logger.info("Parsing currency value:", value, "→", parsed);
    return parsed;
  };

  const getTotalSocialSecurityIncome = () => {
    // logger.group("💰 Calculating Social Security Income");
    const total = (incomeData || [])
      .filter((income) => !income.deleted && income.income_type_id === 1)
      .reduce((total, income) => {
        // logger.group(`Processing Social Security Income Entry`);
        // logger.info("Income entry:", income);

        let amount = 0;
        if (income.projected_annual_total) {
          amount = parseCurrencyValue(income.projected_annual_total);
          // logger.info("Using projected annual total:", formatAsCurrency(amount));
        } else if (income.annual_total) {
          amount = parseCurrencyValue(income.annual_total);
          // logger.info("Using annual total:", formatAsCurrency(amount));
        } else if (income.gross_amount) {
          const baseAmount = parseCurrencyValue(income.gross_amount);
          const multiplier = getFrequencyMultiplier(income);
          amount = baseAmount * multiplier;
          // logger.info("Calculated from gross amount:");
          // logger.info("  Base amount:", formatAsCurrency(baseAmount));
          // logger.info("  Multiplier:", multiplier);
          // logger.info("  Annual amount:", formatAsCurrency(amount));
        }

        // logger.info("Running total:", formatAsCurrency(total + amount));
        // logger.groupEnd();
        return total + amount;
      }, 0);

    // logger.info("🔍 Final Total Social Security Income:", formatAsCurrency(total));
    // logger.groupEnd();
    return total;
  };

  const getProjectedGrossHouseholdIncome = () => {
    // logger.group("💰 Calculating Projected Gross Household Income");
    let total;

    if (
      totalFromIncomeAnalysis !== undefined &&
      totalFromIncomeAnalysis !== null
    ) {
      total = parseCurrencyValue(totalFromIncomeAnalysis);
      // logger.info("Using total from income analysis:", formatAsCurrency(total));
    } else {
      total = (incomeData || [])
        .filter((income) => !income.deleted)
        .reduce((sum, income) => {
          // logger.group(`Processing Income Entry`);
          // logger.info("Income entry:", income);

          let amount = 0;
          if (income.projected_annual_total) {
            amount = parseCurrencyValue(income.projected_annual_total);
            // logger.info("Using projected annual total:", formatAsCurrency(amount));
          } else if (income.annual_total) {
            amount = parseCurrencyValue(income.annual_total);
            // logger.info("Using annual total:", formatAsCurrency(amount));
          } else if (income.gross_amount) {
            const baseAmount = parseCurrencyValue(income.gross_amount);
            const multiplier = getFrequencyMultiplier(income);
            amount = baseAmount * multiplier;
            // logger.info("Calculated from gross amount:");
            // logger.info("  Base amount:", formatAsCurrency(baseAmount));
            // logger.info("  Multiplier:", multiplier);
            // logger.info("  Annual amount:", formatAsCurrency(amount));
          }

          // logger.info("Running total:", formatAsCurrency(sum + amount));
          // logger.groupEnd();
          return sum + amount;
        }, 0);
    }

    // logger.info("🔍 Final Total Projected Gross Household Income:", formatAsCurrency(total));
    // logger.groupEnd();
    return total;
  };

  const handleRothConversionChange = (value) => {
    // logger.group("💰 Handling Roth Conversion Change");
    const numericValue = value || 0;
    // logger.info("New Roth conversion value:", formatAsCurrency(numericValue));
    setRothConversion(numericValue);
    // No need to call recalculateAll here as the useEffect will handle it
    // logger.groupEnd();
  };

  const calculateProjectedAGI = () => {
    // logger.group("📊 AGI Calculation Steps");

    const totalSocialSecurity = getTotalSocialSecurityIncome();
    // logger.info("Step 1: Total Social Security:", formatAsCurrency(totalSocialSecurity));

    const A = totalSocialSecurity * 0.5;
    // logger.info("Step 2A: 50% of Social Security:", formatAsCurrency(A));

    const totalIncome = getProjectedGrossHouseholdIncome();
    // Include Roth conversion in non-Social Security income
    const B = totalIncome - totalSocialSecurity + rothConversion;
    // logger.info("Step 2B: Non-Social Security Income (including Roth):", formatAsCurrency(B));

    const C = A + B;
    // logger.info("Step 2C: Provisional Income before adjustments:", formatAsCurrency(C));

    const D = 0;
    const E = C - D;
    // logger.info("Step 2E: Adjusted provisional income:", formatAsCurrency(E));

    const F = taxFilingStatus === "Married Filing Jointly" ? 32000 : 25000;
    // logger.info("Step 3: Base amount for filing status:", formatAsCurrency(F));

    const G = F > E ? 0 : E - F;
    // logger.info("Step 3G: Excess over base amount:", formatAsCurrency(G));

    const H = taxFilingStatus === "Married Filing Jointly" ? 12000 : 9000;
    // logger.info("Step 4: Second tier threshold:", formatAsCurrency(H));

    const I = Math.max(0, G - H);
    // logger.info("Step 4I: Amount over second tier:", formatAsCurrency(I));

    const J = Math.min(G, H);
    // logger.info("Step 4J: Amount in first tier:", formatAsCurrency(J));

    const K = J * 0.5;
    // logger.info("Step 5K: 50% of first tier:", formatAsCurrency(K));

    const L = Math.min(K, A);
    // logger.info("Step 5L: Limited by 50% of total benefits:", formatAsCurrency(L));

    const M = I * 0.85;
    // logger.info("Step 5M: 85% of second tier:", formatAsCurrency(M));

    const N = L + M;
    // logger.info("Step 5N: Total taxable portion:", formatAsCurrency(N));

    const O = totalSocialSecurity * 0.85;
    // logger.info("Step 6O: Maximum taxable benefits:", formatAsCurrency(O));

    const P = Math.min(O, N);
    // logger.info("Step 6P: Final taxable Social Security:", formatAsCurrency(P));

    const AGI =
      P +
      (totalIncome - totalSocialSecurity) +
      projectedCapitalGains +
      rothConversion;
    // logger.info("Step 7: Final AGI Calculation:");
    // logger.info("  - Taxable Social Security:", formatAsCurrency(P));
    // logger.info("  - Non-Social Security Income:", formatAsCurrency(totalIncome - totalSocialSecurity));
    // logger.info("  - Capital Gains:", formatAsCurrency(projectedCapitalGains));
    // logger.info("  - Roth Conversion:", formatAsCurrency(rothConversion));
    // logger.info("  = Total AGI:", formatAsCurrency(AGI));

    const ssTaxability =
      totalSocialSecurity > 0 ? Math.min(P / totalSocialSecurity, 1) : 0;
    values.currentSSTaxability = `${(ssTaxability * 100).toFixed(2)}%`;
    // logger.info("Social Security Taxability:", values.currentSSTaxability);

    // logger.groupEnd();
    return AGI;
  };

  const parseTaxBracketRange = (range) => {
    if (range.includes("or more")) {
      return Infinity;
    }
    const match = range.match(/\$([0-9,]+)/g);
    if (match && match.length > 1) {
      return parseFloat(match[1].replace(/[$,]/g, ""));
    }
    return 0;
  };

  const getTaxBracketsForStatus = () => {
    const statusMap = {
      Single: "single",
      "Married Filing Jointly": "married",
      "Head of Household": "head",
      "Married Filing Separately": "marriedseparate",
    };

    const brackets = taxBrackets || [];
    const statusKey = statusMap[taxFilingStatus];

    if (!statusKey) {
      // logger.error("Invalid filing status:", taxFilingStatus);
      return [];
    }

    // Map brackets to include ceiling values and keep rate as percentage string
    return brackets.map((bracket) => {
      const range = bracket[statusKey];
      let ceiling;
      
      if (range.includes("or more")) {
        ceiling = Infinity;
      } else {
        const [min, max] = range.split(" to ").map(val => 
          parseFloat(val.replace(/[^0-9.]/g, ''))
        );
        ceiling = max;
      }

      return {
        ceiling,
        rate: bracket.rate, // Keep original rate string (e.g. "22%")
        rateDecimal: parseFloat(bracket.rate.replace('%', '')) / 100 // Add decimal version for calculations
      };
    }).sort((a, b) => a.ceiling - b.ceiling);
  };

  const getCurrentTaxBracketCeiling = (currentTaxableIncome) => {
    const brackets = getTaxBracketsForStatus();
    for (const bracket of brackets) {
      if (currentTaxableIncome <= bracket.ceiling) {
        return bracket.ceiling;
      }
    }
    return Infinity;
  };

  const getNextTaxBracketCeiling = (currentTaxableIncome) => {
    const brackets = getTaxBracketsForStatus();
    let foundCurrent = false;
    
    for (const bracket of brackets) {
      if (foundCurrent) {
        return bracket.ceiling;
      }
      if (currentTaxableIncome <= bracket.ceiling) {
        foundCurrent = true;
      }
    }
    return Infinity;
  };

  const getMarginalTaxRate = (taxableIncome) => {
    const brackets = getTaxBracketsForStatus();
    for (const bracket of brackets) {
      if (taxableIncome <= bracket.ceiling) {
        return bracket.rate; // Return percentage string
      }
    }
    return brackets[brackets.length - 1]?.rate || "0%";
  };

  const calculateTaxableIncome = (
    AGI,
    deduction65,
    blindDeduction,
    standardDeduction,
  ) => {
    return Math.max(
      0,
      AGI - (deduction65 + blindDeduction + standardDeduction),
    );
  };

  const calculateAGIWithTestRothAmount = (testRothAmount) => {
    // logger.group("📊 AGI Calculation with Test Roth Amount");
    
    const totalSocialSecurity = getTotalSocialSecurityIncome();
    // logger.info("Step 1: Total Social Security:", formatAsCurrency(totalSocialSecurity));

    const A = totalSocialSecurity * 0.5;
    // logger.info("Step 2A: 50% of Social Security:", formatAsCurrency(A));

    const totalIncome = getProjectedGrossHouseholdIncome();
    // Include test Roth conversion in non-Social Security income
    const B = totalIncome - totalSocialSecurity + testRothAmount;
    // logger.info("Step 2B: Non-Social Security Income (including test Roth):", formatAsCurrency(B));

    const C = A + B;
    // logger.info("Step 2C: Provisional Income before adjustments:", formatAsCurrency(C));

    const D = 0;
    const E = C - D;
    // logger.info("Step 2E: Adjusted provisional income:", formatAsCurrency(E));

    const F = taxFilingStatus === "Married Filing Jointly" ? 32000 : 25000;
    // logger.info("Step 3: Base amount for filing status:", formatAsCurrency(F));

    const G = F > E ? 0 : E - F;
    // logger.info("Step 3G: Excess over base amount:", formatAsCurrency(G));

    const H = taxFilingStatus === "Married Filing Jointly" ? 12000 : 9000;
    // logger.info("Step 4: Second tier threshold:", formatAsCurrency(H));

    const I = Math.max(0, G - H);
    // logger.info("Step 4I: Amount over second tier:", formatAsCurrency(I));

    const J = Math.min(G, H);
    // logger.info("Step 4J: Amount in first tier:", formatAsCurrency(J));

    const K = J * 0.5;
    // logger.info("Step 5K: 50% of first tier:", formatAsCurrency(K));

    const L = Math.min(K, A);
    // logger.info(
    //   "Step 5L: Limited by 50% of total benefits:",
    //   formatAsCurrency(L),
    // );

    const M = I * 0.85;
    // logger.info("Step 5M: 85% of second tier:", formatAsCurrency(M));

    const N = L + M;
    // logger.info("Step 5N: Total taxable portion:", formatAsCurrency(N));

    const O = totalSocialSecurity * 0.85;
    // logger.info("Step 6O: Maximum taxable benefits:", formatAsCurrency(O));

    const P = Math.min(O, N);
    // logger.info("Step 6P: Final taxable Social Security:", formatAsCurrency(P));

    const AGI =
      P +
      (totalIncome - totalSocialSecurity) +
      projectedCapitalGains +
      testRothAmount;
    // logger.info("Step 7: Final AGI Calculation:");
    // logger.info("  - Taxable Social Security:", formatAsCurrency(P));
    // logger.info(
    //   "  - Non-Social Security Income:",
    //   formatAsCurrency(totalIncome - totalSocialSecurity),
    // );
    // logger.info("  - Capital Gains:", formatAsCurrency(projectedCapitalGains));
    // logger.info("  - Test Roth Conversion:", formatAsCurrency(testRothAmount));
    // logger.info("  = Total AGI:", formatAsCurrency(AGI));

    // logger.groupEnd();
    return AGI;
  };

  const findRangeIndex = (ranges, value) => {
    // logger.group("📊 Finding Income Range");
    for (let i = ranges.length - 1; i >= 0; i--) {
      if (value >= ranges[i]) {
        // logger.info(
        //   `Found range: ${formatAsCurrency(ranges[i])} for value: ${formatAsCurrency(value)}`,
        // );
        // logger.groupEnd();
        return i;
      }
    }
    // logger.groupEnd();
    return 0;
  };

  const getSSTaxabilityRange = (ssPercentage) => {
    // logger.group("📊 Determining SS Taxability Range");
    const percentage = parseFloat(ssPercentage);
    let range;

    if (percentage <= 25) {
      range = "LOW";
    } else if (percentage <= 50) {
      range = "MEDIUM";
    } else {
      range = "HIGH";
    }

    // logger.info(`SS Taxability ${percentage}% falls in ${range} range`);
    // logger.groupEnd();
    return range;
  };

  const findOptimalRothConversion = (targetCeiling, isNextBracket = false) => {
    // logger.group("🎯 Finding Optimal Roth Conversion using Lookup Tables");

    // Get current taxable income without any conversion
    const baseAGI = calculateAGIWithTestRothAmount(0);
    const baseTaxableIncome = calculateTaxableIncome(
      baseAGI,
      values.deduction65,
      values.blindDeduction,
      values.standardDeduction
    );

    // Get current taxable income with existing Roth conversion
    const currentAGI = calculateAGIWithTestRothAmount(rothConversion);
    const currentTaxableIncome = calculateTaxableIncome(
      currentAGI,
      values.deduction65,
      values.blindDeduction,
      values.standardDeduction
    );

    // logger.info("Base taxable income (no conversion):", formatAsCurrency(baseTaxableIncome));
    // logger.info("Current taxable income (with conversion):", formatAsCurrency(currentTaxableIncome));
    // logger.info("Target ceiling:", formatAsCurrency(targetCeiling));
    // logger.info("Calculating for:", isNextBracket ? "next bracket" : "current bracket");

    // If we're already over the target ceiling, suggest no additional conversion
    if (currentTaxableIncome >= targetCeiling) {
      // logger.info("Already at or over target ceiling");
      // logger.groupEnd();
      return 0;
    }

    // Get the available space in the bracket
    const availableSpace = Math.max(0, targetCeiling - currentTaxableIncome);
    // logger.info("Available space:", formatAsCurrency(availableSpace));

    // Start with a conservative estimate
    const initialEstimate = Math.floor(availableSpace * 0.9 * 100) / 100; // 90% of available space as initial guess

    // Precise calibration step
    const calibrateConversion = (estimate) => {
      // logger.group("🎯 Calibrating Conversion Amount");
      let testAmount = estimate;
      let iterations = 0;
      const maxIterations = 15;
      const tolerance = 0.01; // 1 cent tolerance
      let bestAmount = testAmount;
      let bestGap = Number.MAX_VALUE;
      let lastGap = Number.MAX_VALUE;

      while (iterations < maxIterations) {
        iterations++;
        
        const testAGI = calculateAGIWithTestRothAmount(rothConversion + testAmount);
        const testTaxableIncome = calculateTaxableIncome(
          testAGI,
          values.deduction65,
          values.blindDeduction,
          values.standardDeduction
        );

        const gap = targetCeiling - testTaxableIncome;

        // Track the best result we've seen
        if (gap >= 0 && gap < Math.abs(bestGap)) {
          bestAmount = testAmount;
          bestGap = gap;
        }

        if (Math.abs(gap) <= tolerance) {
          return testAmount;
        }

        // If we're oscillating (gap changed sign), use smaller adjustments
        const isOscillating = gap * lastGap < 0;
        lastGap = gap;

        if (gap < 0) {
          // We went over, scale back
          const scaleFactor = (targetCeiling - currentTaxableIncome) / (testTaxableIncome - currentTaxableIncome);
          const adjustment = isOscillating ? 0.999 : 0.995;
          testAmount = Math.floor(testAmount * scaleFactor * adjustment * 100) / 100;
        } else {
          // We have room to grow, but be conservative
          const portionOfGap = isOscillating ? 0.01 : // If oscillating, tiny steps
                              gap < 0.1 ? 0.02 : // Very close, very small steps
                              gap < 1 ? 0.05 : // Close, small steps
                              gap < 10 ? 0.1 : // Getting closer, careful steps
                              0.25; // Far away, larger steps
          const additionalAmount = Math.floor(gap * portionOfGap * 100) / 100;
          testAmount += additionalAmount;
        }
      }

      return bestAmount;
    };

    // Calibrate the conversion amount
    const calibratedAmount = calibrateConversion(initialEstimate);
    // logger.info("Final calibrated amount:", formatAsCurrency(calibratedAmount));

    // logger.groupEnd();
    return calibratedAmount;
  };

  const recalculateAll = () => {
    // logger.group("🔄 Recalculating All Values");

    const blindDeduction = calculateBlindDeduction();
    const deduction65 = calculateDeduction65();
    const AGI = calculateProjectedAGI();
    const standardDeduction = getStandardDeduction(taxFilingStatus);

    // logger.info("Standard Deduction:", formatAsCurrency(standardDeduction));
    // logger.info("Current Roth Conversion:", formatAsCurrency(rothConversion));

    const taxableIncome = calculateTaxableIncome(
      AGI,
      deduction65,
      blindDeduction,
      standardDeduction,
    );
    // logger.info("Taxable Income Calculation:");
    // logger.info("  AGI:", formatAsCurrency(AGI));
    // logger.info("  - 65+ Deduction:", formatAsCurrency(deduction65));
    // logger.info("  - Blind Deduction:", formatAsCurrency(blindDeduction));
    // logger.info("  - Standard Deduction:", formatAsCurrency(standardDeduction));
    // logger.info("  = Taxable Income:", formatAsCurrency(taxableIncome));

    const currentBracketCeiling = getCurrentTaxBracketCeiling(taxableIncome);
    const marginalRate = getMarginalTaxRate(taxableIncome);

    // Calculate optimal conversion amounts using lookup tables
    const optimalCurrentBracket = findOptimalRothConversion(
      currentBracketCeiling,
    );

    const nextBracketCeiling = getNextTaxBracketCeiling(taxableIncome);
    const nextMarginalRate = getMarginalTaxRate(nextBracketCeiling);
    const optimalThroughNextBracket = findOptimalRothConversion(
      nextBracketCeiling,
      true,
    );

    // logger.info("Tax Bracket Information:");
    // logger.info("  Current Bracket Ceiling:", formatAsCurrency(currentBracketCeiling));
    // logger.info("  Marginal Tax Rate:", marginalRate);
    // logger.info("  Optimal conversion to ceiling:", formatAsCurrency(optimalCurrentBracket));
    // logger.info("  Next Bracket Ceiling:", formatAsCurrency(nextBracketCeiling));
    // logger.info("  Next Marginal Rate:", nextMarginalRate);
    // logger.info("  Optimal conversion through next:", formatAsCurrency(optimalThroughNextBracket));

    setValues((prevValues) => {
      const newValues = {
        ...prevValues,
        projectedAGI: AGI,
        projectedMAGI: AGI,
        deduction65,
        blindDeduction,
        standardDeduction,
        currentTaxableIncome: taxableIncome,
        currentTaxBracketCeiling: currentBracketCeiling,
        grossConversionMaxCurrentBracket: `${formatAsCurrency(optimalCurrentBracket)} (${marginalRate})`,
        grossConversionMaxThroughNextBracket: `${formatAsCurrency(optimalThroughNextBracket)} (${nextMarginalRate})`,
      };
      // logger.info("Updated values:", newValues);
      return newValues;
    });

    // logger.groupEnd();
  };

  const getStandardDeduction = (filingStatus) => {
    if (!deductionsData || !Array.isArray(deductionsData)) {
      logger.error("No valid deductions data provided");
      return 0;
    }

    const deduction = deductionsData.find((d) => d.status === filingStatus);
    if (!deduction) {
      logger.error("No deduction found for filing status:", filingStatus);
      return 0;
    }

    return parseFloat(deduction.standard.replace(/[$,]/g, ""));
  };

  // Define row data with correct order and properties
  const getTableRows = () => [
    {
      label: "Tax Filing Status",
      value: taxFilingStatus,
      info: "Your tax filing status affects your standard deduction and tax brackets",
    },
    {
      label: "Blind Exemption",
      value: blindDeductionCheckboxes,
      info: "Check this box if you or your spouse are legally blind",
      isCheckbox: true,
      isEditable: true,
      onChange: handleCheckboxChange,
    },
    {
      label: "Projected Capital Gains",
      value: projectedCapitalGains,
      info: "Expected capital gains for the tax year",
      isCurrency: true,
      isEditable: true,
      onChange: (value) => setProjectedCapitalGains(value),
    },
    {
      label: "Potential Roth IRA Conversion",
      value: rothConversion,
      info: "Amount you're considering converting to Roth IRA",
      isCurrency: true,
      isEditable: true,
      onChange: handleRothConversionChange,
    },
    {
      label: "Projected Adjusted Gross Income (AGI)",
      value: formatAsCurrency(values.projectedAGI),
      info: "Your estimated AGI including all income sources",
    },
    {
      label: "Projected Modified Adjusted Gross Income (MAGI)",
      value: formatAsCurrency(values.projectedMAGI),
      info: "Your MAGI is used to determine IRA contribution limits",
    },
    {
      label: "65+ Deduction",
      value: formatAsCurrency(values.deduction65),
      info: "Additional standard deduction if you or your spouse are 65 or older",
    },
    {
      label: "Blind Deduction",
      value: formatAsCurrency(values.blindDeduction),
      info: "Additional standard deduction for blindness",
    },
    {
      label: "Standard Deduction",
      value: formatAsCurrency(values.standardDeduction),
      info: "Your standard deduction based on filing status",
    },
    {
      label: "Current Taxable Income",
      value: formatAsCurrency(values.currentTaxableIncome),
      info: "Your taxable income after all deductions",
    },
    {
      label: "Current Tax Bracket Ceiling",
      value: formatAsCurrency(values.currentTaxBracketCeiling),
      info: "The upper limit of your current tax bracket",
    },
    {
      label: "Social Security Taxability",
      value: values.currentSSTaxability,
      info: "Percentage of Social Security benefits subject to tax",
    },
    {
      label: "Gross Conversion Max Current Bracket",
      value: values.grossConversionMaxCurrentBracket,
      info: "Maximum Roth conversion amount within current tax bracket",
    },
    {
      label: "Gross Conversion Max Through Next Bracket",
      value: values.grossConversionMaxThroughNextBracket,
      info: "Maximum Roth conversion amount through next tax bracket",
    },
  ];

  return (
    <AnalysisContainer isDarkMode={isDarkMode}>
      {!hideTitle && <Title isDarkMode={isDarkMode}>{year} Tax Analysis</Title>}
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <Th isDarkMode={isDarkMode}>Item</Th>
              <Th isDarkMode={isDarkMode}>Value</Th>
            </tr>
          </thead>
          <tbody>
            {getTableRows().map((row, index) => (
              <EnhancedTableRow
                key={index}
                label={row.label}
                value={row.value}
                info={row.info}
                isEditable={row.isEditable}
                isCurrency={row.isCurrency}
                isCheckbox={row.isCheckbox}
                onChange={row.onChange}
                isDarkMode={isDarkMode}
              />
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </AnalysisContainer>
  );
};

export default HouseholdTaxAnalysis;
